
import { Component, Vue, Watch } from 'vue-property-decorator'
import { gender } from '@/utils/dictionary'
import http from '@/utils/request'
import { getDigisign, checkDigisign, getUserInfo, getPlatformInfo, setDigisignInfo } from '@/utils/user'
import { sleep } from '@/utils'
import { AddressItem, UploadFiles, DigisignResponse } from '../register.interface'
import '../register.scss'
import dayjs from 'dayjs'
import { ElUploadInternalFileDetail, HttpRequestOptions } from 'element-ui/types/upload'
import { Upload } from 'element-ui'
import Loading from '../Loading.vue'
import StepsIndividual from '../StepsIndividual.vue'
import { ContentType } from '@/utils/request.interface'

@Component({
  components: { StepsIndividual, Loading }
})
export default class extends Vue {
  private form = {
    sex: '',
    postcode: '',
    birthday: '',
    province: '',
    city: '',
    street: '',
    community: '',
    address: '',
    noktpPhoto: '',
    selfPhoto: ''
  }
  //

  private files: UploadFiles = {
    noktpPhoto: {
      loading: false,
      list: [],
      file_id: '',
      file_name: ''
    },
    selfPhoto: {
      loading: false,
      list: [],
      file_id: '',
      file_name: ''
    }
  }

  private genderOptions = gender
  private provinceOptions: AddressItem[] = []
  private cityOptions: AddressItem[] = []
  private streetOptions: AddressItem[] = []
  private communityOptions: AddressItem[] = []
  private cachedEmail = ''
  private cachedEmailCode = ''
  private thirdPartySrc = ''
  private submitDisabled = true
  private submitLoading = false
  private sendDigisignEmailLoading = false
  private showThirdPartyDialog = false
  private showLoading = false
  private loadingStatus = '' // Default value is empty; success: success
  private message = ''

  @Watch('form', { deep: true })
  onFormChange() {
    // eslint-disable-next-line
    const { community, ...params } = this.form
    const valid = Object.values(params).every((value) => {
      return value !== '' && value !== null && value !== undefined
    })

    this.submitDisabled = !valid
  }

  created() {
    this.getDigisignData()
    this.getAddressData()
  }

  mounted() {
    if (process.env.NODE_ENV === 'development') {
      setTimeout(() => {
        this.testForm()
      }, 200)
    }
  }

  testForm() {
    this.form.sex = '1'
    this.form.postcode = '51182'
    this.form.selfPhoto = 'S3dbc2c79d-ab45-4402-bae4-fd8ab38cda66.jpg'
    this.form.noktpPhoto = 'S34b35ab89-9d98-49c9-bfb1-9d28501f468c.jpg'
    this.form.address = 'jl sudirman RT 002/005'
  }

  async getDigisignData() {
    const data = await getDigisign()
    if (data) {
      if (data.birthday) {
        const [DD, MM, YYYY] = data.birthday.split('-')
        data.birthday = new Date([YYYY, MM, DD].join('-'))
      }
      this.form.sex = data.sex
      this.form.postcode = data.postcode
      this.form.birthday = data.birthday
      this.form.province = data.province
      this.form.city = data.city
      this.form.street = data.street
      this.form.community = data.community
      this.form.address = data.address
      this.form.noktpPhoto = data.noktpPhoto
      this.form.selfPhoto = data.selfPhoto
    }
  }

  async getAddressData() {
    this.provinceOptions = await this.getRegion('ID')
    if (this.form.province) {
      this.form.province = this.findAddresCode(this.provinceOptions, this.form.province)

      this.cityOptions = await this.getRegion(this.form.province)
      if (this.form.city) {
        this.form.city = this.findAddresCode(this.cityOptions, this.form.city)

        this.streetOptions = await this.getRegion(this.form.city)
        if (this.form.street) {
          this.form.street = this.findAddresCode(this.streetOptions, this.form.street)

          this.communityOptions = await this.getRegion(this.form.street)
          if (this.form.community) {
            this.form.community = this.findAddresCode(this.communityOptions, this.form.community)
          }
        }
      }
    }
  }

  async getProvince() {
    const result = await this.getRegion('ID')
    this.provinceOptions = result
    this.cityOptions = []
    this.streetOptions = []
    this.communityOptions = []
    this.form.province = ''
    this.form.city = ''
    this.form.street = ''
    this.form.community = ''
  }

  async getCity(code: string) {
    const result = await this.getRegion(code)
    this.cityOptions = result
    this.streetOptions = []
    this.communityOptions = []
    this.form.city = ''
    this.form.street = ''
    this.form.community = ''
  }

  async getStreet(code: string) {
    const result = await this.getRegion(code)
    this.streetOptions = result
    this.communityOptions = []
    this.form.street = ''
    this.form.community = ''
  }

  async getCommunity(code: string) {
    const result = await this.getRegion(code)
    this.communityOptions = result
    this.form.community = ''
  }

  async getRegion(addressNo: string) {
    const data: AddressItem[] = await http.post('/user/queryRegion', { addressNo })
    return Promise.resolve(data)
  }

  upload(param: ElUploadInternalFileDetail, field: 'noktpPhoto' | 'selfPhoto') {
    this.files[field].loading = true
    const userInfoMap = getUserInfo('userInfoMap')
    const formData = new FormData()
    formData.append('userNo', userInfoMap.userNo)
    formData.append('file', param as unknown as File)

    http
      .post('/user/uploadFile', formData, {
        headers: {
          'Content-Type': ContentType.FORM_DATA
        }
      })
      .then((data) => {
        this.$notify.success({ title: 'Success', message: '' })
        this.files[field].file_id = data.file_id
        this.form[field] = param.name
      })
      .catch(() => {
        this.form[field] = ''
      })
      .finally(() => {
        this.files[field].loading = false
      })
  }

  /**
   * Removes dangerous characters from a file name and replaces them with an underscore.
   *
   * @param {string} fileName - The original file name to sanitize.
   * @returns {string} The sanitized file name.
   */
  removeDangerousCharacters(fileName: string): string {
    let sanitized = fileName.replace(/^(\.\.?(\/|\\))+/, '')

    // eslint-disable-next-line no-control-regex
    const dangerousChars = /[<>:"'/\\|?*\x00-\x1F\x7F]+/g
    sanitized = sanitized.replace(dangerousChars, '_')

    sanitized = sanitized.replace(/\0/g, '')

    const reservedNames = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i
    if (reservedNames.test(sanitized)) {
      sanitized = '_' + sanitized
    }

    if (sanitized === '' || sanitized === '.') {
      sanitized = 'unnamed_file'
    }

    return sanitized
  }

  /**
   * Check file type - specific accepted extensions include: .jpg, .jpeg, .png
   */
  isFileTypeValid(file: ElUploadInternalFileDetail, field: string) {
    const mimeTypeCheck = file.raw.type === 'image/jpeg' ||
      file.raw.type === 'image/jpg' ||
      file.raw.type === 'image/png'
    const fileName = field === 'selfPhoto' ? 'foto selfie' : 'foto KTP'
    this.submitDisabled = !mimeTypeCheck
    this.message = mimeTypeCheck ? '' : `Format ${fileName} hanya mendukung jpg, jpeg, png` // TODO
    return mimeTypeCheck
  }

  /**
   * Checks if a file's size is within the specified limit of 2MB.
   *
   * @param {File} file - The file to check.
   * @returns {void}
   */
  isFileSizeValid(file: File, field: string): boolean {
    const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB in bytes
    const isValidSize = file.size <= MAX_FILE_SIZE
    const fileName = field === 'selfPhoto' ? 'foto selfie' : 'foto KTP'

    this.submitDisabled = !isValidSize
    this.message = isValidSize ? '' : `Ukuran ${fileName} tidak boleh lebih dari 2Mb`
    return isValidSize
  }

  fileChange(file: ElUploadInternalFileDetail | any, field: 'noktpPhoto' | 'selfPhoto') {
    ;(this.$refs[field] as Upload).clearFiles()
    this.files[field].file_name = file.name
    this.files[field].list = [{ name: file.name, url: file.url }]
    const santized = this.removeDangerousCharacters(file.name)
    if (!this.isFileTypeValid(file, field)) {
      return
    }
    if (!this.isFileSizeValid(file as unknown as File, field)) {
      return
    }
    file.name = santized
    this.upload(file.raw, field)
  }

  findAddresName(items: AddressItem[], code: string) {
    const result = items.find((item) => item.code === code)
    return result ? result.name : ''
  }

  findAddresCode(items: AddressItem[], name: string) {
    const result = items.find((item) => item.name === name)
    return result ? result.code : ''
  }

  async onSubmit() {
    const params: any = { ...this.form }

    params.birthday = dayjs(this.form.birthday).format('DD-MM-YYYY')
    params.noktpPhoto = this.files.noktpPhoto.file_id
    params.selfPhoto = this.files.selfPhoto.file_id
    params.province = this.findAddresName(this.provinceOptions, this.form.province)
    params.city = this.findAddresName(this.cityOptions, this.form.city)
    params.street = this.findAddresName(this.streetOptions, this.form.street)
    params.community = this.findAddresName(this.communityOptions, this.form.community)
    params.noktp = getPlatformInfo('identity')
    params.permanentAddress = params.street
    params.userNo = getUserInfo('userInfoMap').userNo
    params.mid = params.userNo
    params.email = getUserInfo('email')
    params.tenantId = getUserInfo('tenantId')

    // Submit buttonloading
    this.submitLoading = true
    this.showLoading = true

    /* console.log(JSON.stringify(params, null, 2)) */
    /* return false */

    try {
      const result = await http.post<DigisignResponse>('/sign/register', params)
      setDigisignInfo(params)
      if (result) {
        if (result.link) {
          await sleep(500)
          this.showLoading = false
          this.thirdPartySrc = result.link
          this.showThirdPartyDialog = true

          // Poll the background to see if digisign activation is successful
          // eslint-disable-next-line
          const timer = setInterval(async () => {
            let isActive = await checkDigisign()
            if (process.env.NODE_ENV === 'development') {
              isActive = true
            }
            if (isActive) {
              clearInterval(timer)
              this.loadingStatus = 'success'
              this.showLoading = true
              await sleep(2000)
              this.showLoading = false
              await this.$router.push({ path: '/register/RDL' })
            }
          }, 5 * 1000)
        } else {
          await sleep(2000)
          this.showLoading = false
          this.$router.push({ path: '/register/RDL' })
        }
      } else {
        this.$notify({ title: 'Error', message: '' })
      }
    } catch (e) {
      this.showLoading = false
      // handle error
    } finally {
      this.submitLoading = false
    }
  }
}
